import { useState, useEffect } from 'react';
import { createLogger } from '@/lib/logger';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Award, Search, TrendingUp, Calendar , ChevronDown, ChevronUp } from 'lucide-react-native';
import SkeletonRow from '@/components/SkeletonRow';
import { SafeAreaView } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import { supabase } from '@/lib/supabase';
import EmptyState from '@/components/EmptyState';
import PRProgressionChart from '@/components/PRProgressionChart';

import { useUnits } from '@/lib/useUnits';

const log = createLogger('MY_PRS');

interface PREntry {
  id: string;
  exercise_name: string;
  weight: number;
  reps: number;
  sets: number;
  created_at: string;
  is_verified: boolean;
  video_url?: string;
  source_units: string; // the unit the workout was logged in
}

export default function MyPRsScreen() {
  const router = useRouter();
  const { profile } = useAuth();
  const { theme } = useTheme();
  const { formatWeight } = useUnits();
  const [prs, setPRs] = useState<PREntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'recent' | 'weight'>('recent');
  const [expandedPR, setExpandedPR] = useState<string | null>(null);

  useEffect(() => {
    loadPRs();
  }, []);

  const loadPRs = async () => {
    if (!profile) return;

    try {
      const { data: workouts } = await supabase
        .from('workouts')
        .select('*')
        .eq('user_id', profile.id)
        .order('performed_at', { ascending: false });

      if (!workouts) {
        setPRs([]);
        setLoading(false);
        return;
      }

      const prMap = new Map<string, PREntry>();

      workouts.forEach((workout: any) => {
        const workoutUnits = workout.payload?.units ?? 'lbs';
        workout.payload.exercises?.forEach((exercise: any) => {
          exercise.sets.forEach((set: any) => {
            if (set.is_pr) {
              const key = exercise.name;
              const existing = prMap.get(key);

              if (!existing || set.weight > existing.weight) {
                prMap.set(key, {
                  id: `${workout.id}-${exercise.exercise_id}`,
                  exercise_name: exercise.name,
                  weight: set.weight,
                  reps: set.reps,
                  sets: 1,
                  created_at: workout.performed_at,
                  is_verified: false,
                  video_url: set.media_url,
                  source_units: workoutUnits,
                });
              }
            }
          });
        });
      });

      setPRs(Array.from(prMap.values()));
    } catch (error) {
      log.error('Error loading PRs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPRs = prs
    .filter((pr) =>
      searchQuery ? pr.exercise_name.toLowerCase().includes(searchQuery.toLowerCase()) : true
    )
    .sort((a, b) => {
      if (sortBy === 'weight') {
        return b.weight - a.weight;
      }
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

  if (loading) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <View style={styles.centerContainer}>
          <SkeletonRow rows={4} />
        </View>
      </View>
    );
  }

  return (
    <SafeAreaView style={{ backgroundColor: theme.colors.background, flex: 1 }} edges={['top']}>
    <View style={[styles.container]}>
      <LinearGradient
        colors={[theme.colors.background, theme.colors.surfaceVariant]}
        style={[styles.header, { borderBottomColor: theme.colors.border }]}>
        <View style={styles.headerTop}>
          <View style={{ width: 28 }} />
          <Text style={[styles.headerTitle, { color: theme.colors.accent }]}>MY PERSONAL RECORDS</Text>
          <View style={{ width: 28 }} />
        </View>

        <View
          style={[
            styles.statsCard,
            { backgroundColor: theme.colors.surface, borderColor: theme.colors.border },
          ]}>
          <Award size={32} color={theme.colors.warning} />
          <View style={styles.statsInfo}>
            <Text style={[styles.statsValue, { color: theme.colors.text }]}>{prs.length}</Text>
            <Text style={[styles.statsLabel, { color: theme.colors.textSecondary }]}>
              Personal Records
            </Text>
          </View>
        </View>

        <View
          style={[
            styles.searchBar,
            { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.border },
          ]}>
          <Search size={20} color={theme.colors.textSecondary} />
          <TextInput
            style={[styles.searchInput, { color: theme.colors.text }]}
            placeholder="Search exercises..."
            placeholderTextColor={theme.colors.textTertiary}
            value={searchQuery}
            onChangeText={setSearchQuery}
            autoCapitalize="none"
            autoCorrect={false}
          />
        </View>

        <View style={styles.sortButtons}>
          <TouchableOpacity
            style={[
              styles.sortButton,
              {
                backgroundColor: sortBy === 'recent' ? theme.colors.accent : theme.colors.surfaceVariant,
                borderColor: theme.colors.border,
              },
            ]}
            onPress={() => setSortBy('recent')}>
            <Calendar size={16} color={sortBy === 'recent' ? theme.colors.text : theme.colors.textSecondary} />
            <Text
              style={[
                styles.sortButtonText,
                { color: sortBy === 'recent' ? theme.colors.text : theme.colors.textSecondary },
              ]}>
              Recent
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.sortButton,
              {
                backgroundColor: sortBy === 'weight' ? theme.colors.accent : theme.colors.surfaceVariant,
                borderColor: theme.colors.border,
              },
            ]}
            onPress={() => setSortBy('weight')}>
            <TrendingUp size={16} color={sortBy === 'weight' ? theme.colors.text : theme.colors.textSecondary} />
            <Text
              style={[
                styles.sortButtonText,
                { color: sortBy === 'weight' ? theme.colors.text : theme.colors.textSecondary },
              ]}>
              Heaviest
            </Text>
          </TouchableOpacity>
        </View>
      </LinearGradient>

      {filteredPRs.length === 0 ? (
        <View style={styles.centerContainer}>
          <EmptyState
            icon={Award}
            title="No PRs Yet"
            description="Log workouts and mark your personal records to track your progress here."
            actionLabel="Log Workout"
            onAction={() => router.push('/(tabs)/record')}
          />
        </View>
      ) : (
        <ScrollView contentContainerStyle={styles.content}>
          {filteredPRs.map((pr) => {
            const isExpanded = expandedPR === pr.id;
            return (
            <TouchableOpacity
              key={pr.id}
              activeOpacity={0.7}
              onPress={() => setExpandedPR(isExpanded ? null : pr.id)}
              style={[
                styles.prCard,
                { backgroundColor: theme.colors.surface, borderColor: theme.colors.border },
              ]}>
              <View style={styles.prHeader}>
                <View style={styles.prLeft}>
                  <Award size={24} color={theme.colors.warning} />
                  <View style={styles.prInfo}>
                    <Text style={[styles.prExercise, { color: theme.colors.text }]}>
                      {pr.exercise_name}
                    </Text>
                    <Text style={[styles.prDate, { color: theme.colors.textSecondary }]}>
                      {new Date(pr.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </Text>
                  </View>
                </View>

                <View style={styles.prStats}>
                  <Text style={[styles.prWeight, { color: theme.colors.accent }]}>
                    {formatWeight(pr.weight, pr.source_units)}
                  </Text>
                  <Text style={[styles.prReps, { color: theme.colors.textSecondary }]}>
                    × {pr.reps} reps
                  </Text>
                </View>
              </View>

              {pr.video_url && (
                <View style={[styles.videoTag, { backgroundColor: theme.colors.accent + '20' }]}>
                  <Text style={[styles.videoTagText, { color: theme.colors.accent }]}>Video Recorded</Text>
                </View>
              )}

              {/* Expand/collapse hint */}
              <View style={styles.expandHint}>
                {isExpanded ? (
                  <ChevronUp size={16} color={theme.colors.textTertiary} />
                ) : (
                  <ChevronDown size={16} color={theme.colors.textTertiary} />
                )}
              </View>

              {/* Progression chart */}
              {isExpanded && profile?.id && (
                <PRProgressionChart
                  exerciseName={pr.exercise_name}
                  userId={profile.id}
                  unit={profile?.units ?? 'lbs'}
                />
              )}
            </TouchableOpacity>
            );
          })}
        </ScrollView>
      )}
    </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    paddingTop: 12,
    paddingBottom: 20,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '900',
    letterSpacing: 1,
  },
  statsCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    padding: 16,
    borderRadius: 2,
    borderWidth: 1,
    marginBottom: 16,
  },
  statsInfo: {
    flex: 1,
  },
  statsValue: {
    fontSize: 28,
    fontWeight: '900',
  },
  statsLabel: {
    fontSize: 14,
    marginTop: 2,
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 12,
    borderRadius: 2,
    borderWidth: 1,
    marginBottom: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 15,
  },
  sortButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  sortButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 10,
    borderRadius: 0,
    borderWidth: 1,
  },
  sortButtonText: {
    fontSize: 13,
    fontWeight: '700',
  },
  content: {
    padding: 16,
    paddingBottom: 40,
  },
  prCard: {
    borderRadius: 2,
    borderWidth: 1,
    padding: 16,
    marginBottom: 12,
  },
  prHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  prLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  prInfo: {
    flex: 1,
  },
  prExercise: {
    fontSize: 17,
    fontWeight: '700',
    marginBottom: 4,
  },
  prDate: {
    fontSize: 13,
  },
  prStats: {
    alignItems: 'flex-end',
  },
  prWeight: {
    fontSize: 24,
    fontWeight: '900',
  },
  prReps: {
    fontSize: 13,
    marginTop: 2,
  },
  expandHint: {
    alignItems: 'center' as const,
    marginTop: 8,
  },
  videoTag: {
    marginTop: 12,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 2,
    alignSelf: 'flex-start',
  },
  videoTagText: {
    fontSize: 12,
    fontWeight: '700',
  },
});
